import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, X, Image, ExternalLink } from 'lucide-react';

interface PexelsPhoto {
  id: number;
  photographer: string;
  photographer_url: string;
  description: string;
  avg_color: string;
  pexels_url: string;
  urls: {
    medium: string;
    large: string;
    large2x: string;
  };
  dimensions: {
    width: number;
    height: number;
  };
}

interface PexelsImagePickerProps {
  /** Called with the large image URL when user selects a photo. */
  onSelect: (imageUrl: string) => void;
  /** Called when user closes the modal without selecting. */
  onClose: () => void;
  /** Initial search query, derived from the control label or ai_hint. */
  initialQuery?: string;
}

const API_BASE = '/api/site-builder/pexels-search';
const PER_PAGE = 15;

const PexelsImagePicker: React.FC<PexelsImagePickerProps> = ({
  onSelect,
  onClose,
  initialQuery = 'professional business',
}) => {
  const [query, setQuery] = useState(initialQuery);
  const [inputValue, setInputValue] = useState(initialQuery);
  const [photos, setPhotos] = useState<PexelsPhoto[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const fetchPhotos = useCallback(
    async (searchQuery: string, pageNum: number, append: boolean) => {
      if (!searchQuery.trim()) return;

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setError(null);
      }

      try {
        const params = new URLSearchParams({
          query: searchQuery,
          per_page: String(PER_PAGE),
          page: String(pageNum),
        });
        const token = localStorage.getItem('access_token') || '';
        const resp = await fetch(`${API_BASE}?${params}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!resp.ok) {
          const body = await resp.json().catch(() => ({}));
          throw new Error(body.detail || `HTTP ${resp.status}`);
        }

        const data: { photos: PexelsPhoto[]; total_results: number; page: number } =
          await resp.json();

        if (append) {
          setPhotos(prev => [...prev, ...data.photos]);
        } else {
          setPhotos(data.photos);
        }

        setHasMore(pageNum * PER_PAGE < data.total_results);
        setPage(pageNum);
      } catch (err: any) {
        setError(err.message || 'Failed to load images');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Run initial search on mount
  useEffect(() => {
    fetchPhotos(initialQuery, 1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Focus the search input on open
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    setQuery(trimmed);
    setPage(1);
    setPhotos([]);
    fetchPhotos(trimmed, 1, false);
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    fetchPhotos(query, nextPage, true);
  };

  const handleSelect = (photo: PexelsPhoto) => {
    onSelect(photo.urls.large);
    onClose();
  };

  // Skeleton cards during initial load
  const skeletonCount = PER_PAGE;

  return (
    <div
      style={styles.overlay}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <Image size={18} color="var(--primary, #2177FF)" />
            <span style={styles.title}>Search Stock Photos</span>
          </div>
          <button style={styles.closeBtn} onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Search bar */}
        <form onSubmit={handleSearch} style={styles.searchRow}>
          <div style={styles.searchInputWrap}>
            <Search size={15} style={styles.searchIcon} />
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              placeholder="Search Pexels (e.g. professional meeting, modern office)"
              style={styles.searchInput}
            />
          </div>
          <button type="submit" style={styles.searchBtn} disabled={loading}>
            Search
          </button>
        </form>

        {/* Grid / states */}
        <div style={styles.gridContainer}>
          {loading && (
            <div style={styles.grid}>
              {Array.from({ length: skeletonCount }).map((_, i) => (
                <div key={i} style={styles.skeleton} />
              ))}
            </div>
          )}

          {!loading && error && (
            <div style={styles.emptyState}>
              <p style={{ color: 'var(--danger, #ef4444)', marginBottom: 8 }}>{error}</p>
              <button style={styles.retryBtn} onClick={() => fetchPhotos(query, 1, false)}>
                Retry
              </button>
            </div>
          )}

          {!loading && !error && photos.length === 0 && (
            <div style={styles.emptyState}>
              <Image size={40} color="var(--text-secondary, #64748b)" />
              <p style={{ color: 'var(--text-secondary, #64748b)', marginTop: 12 }}>
                No photos found for &ldquo;{query}&rdquo;
              </p>
            </div>
          )}

          {!loading && photos.length > 0 && (
            <div style={styles.grid}>
              {photos.map(photo => (
                <div
                  key={photo.id}
                  style={styles.photoCard}
                  onMouseEnter={() => setHoveredId(photo.id)}
                  onMouseLeave={() => setHoveredId(null)}
                >
                  <img
                    src={photo.urls.medium}
                    alt={photo.description || `Photo by ${photo.photographer}`}
                    style={styles.photoImg}
                    loading="lazy"
                  />

                  {/* Hover overlay */}
                  <div
                    style={{
                      ...styles.overlay2,
                      opacity: hoveredId === photo.id ? 1 : 0,
                    }}
                  >
                    <button
                      style={styles.selectBtn}
                      onClick={() => handleSelect(photo)}
                    >
                      Select
                    </button>
                    <p style={styles.photographerName}>
                      {photo.photographer}
                    </p>
                  </div>

                  {/* Color swatch at bottom */}
                  <div
                    style={{
                      ...styles.colorSwatch,
                      background: photo.avg_color || '#000',
                    }}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Load more */}
          {!loading && hasMore && (
            <div style={styles.loadMoreRow}>
              <button
                style={styles.loadMoreBtn}
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading...' : 'Load more photos'}
              </button>
            </div>
          )}
        </div>

        {/* Footer attribution */}
        <div style={styles.footer}>
          <a
            href="https://www.pexels.com"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.attributionLink}
          >
            <ExternalLink size={11} style={{ marginRight: 4, flexShrink: 0 }} />
            Photos provided by Pexels
          </a>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Inline styles (uses CSS variables where possible for dark-mode compat)
// ---------------------------------------------------------------------------

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.45)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    zIndex: 1200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },

  modal: {
    width: '100%',
    maxWidth: 760,
    maxHeight: '88vh',
    display: 'flex',
    flexDirection: 'column',
    background: 'var(--panel, rgba(255,255,255,0.92))',
    backdropFilter: 'blur(24px)',
    WebkitBackdropFilter: 'blur(24px)',
    border: '1px solid var(--stroke, rgba(15,23,42,0.10))',
    borderRadius: 16,
    boxShadow: '0 24px 60px rgba(0,0,0,0.18)',
    overflow: 'hidden',
  },

  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid var(--stroke, rgba(15,23,42,0.08))',
    flexShrink: 0,
  },

  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },

  title: {
    fontSize: '0.9375rem',
    fontWeight: 600,
    color: 'var(--text, #0f172a)',
  },

  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 4,
    borderRadius: 6,
    color: 'var(--text-secondary, #64748b)',
    display: 'flex',
    alignItems: 'center',
  },

  searchRow: {
    display: 'flex',
    gap: 8,
    padding: '12px 20px',
    borderBottom: '1px solid var(--stroke, rgba(15,23,42,0.08))',
    flexShrink: 0,
  },

  searchInputWrap: {
    flex: 1,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },

  searchIcon: {
    position: 'absolute',
    left: 10,
    color: 'var(--text-secondary, #64748b)',
    pointerEvents: 'none',
  } as React.CSSProperties,

  searchInput: {
    width: '100%',
    padding: '8px 10px 8px 32px',
    border: '1px solid var(--stroke, rgba(15,23,42,0.12))',
    borderRadius: 8,
    fontSize: '0.8125rem',
    background: 'var(--panel-strong, rgba(15,23,42,0.04))',
    color: 'var(--text, #0f172a)',
    outline: 'none',
  },

  searchBtn: {
    padding: '8px 18px',
    background: 'var(--primary, #2177FF)',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: '0.8125rem',
    fontWeight: 600,
    cursor: 'pointer',
    flexShrink: 0,
  },

  gridContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px 20px',
  },

  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 10,
  },

  photoCard: {
    position: 'relative',
    borderRadius: 8,
    overflow: 'hidden',
    aspectRatio: '16/10',
    cursor: 'pointer',
    border: '1px solid var(--stroke, rgba(15,23,42,0.08))',
    background: 'var(--panel-strong, rgba(15,23,42,0.04))',
  },

  photoImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    transition: 'transform 0.2s ease',
  },

  overlay2: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(0,0,0,0.52)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    transition: 'opacity 0.18s ease',
  },

  selectBtn: {
    padding: '7px 20px',
    background: 'var(--primary, #2177FF)',
    color: '#fff',
    border: 'none',
    borderRadius: 999,
    fontSize: '0.8125rem',
    fontWeight: 600,
    cursor: 'pointer',
    letterSpacing: '0.01em',
  },

  photographerName: {
    color: 'rgba(255,255,255,0.85)',
    fontSize: '0.6875rem',
    margin: 0,
    maxWidth: '90%',
    textAlign: 'center',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },

  colorSwatch: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 3,
    opacity: 0.7,
  },

  skeleton: {
    borderRadius: 8,
    aspectRatio: '16/10',
    background: 'linear-gradient(90deg, var(--panel-strong, rgba(15,23,42,0.06)) 25%, var(--stroke, rgba(15,23,42,0.12)) 50%, var(--panel-strong, rgba(15,23,42,0.06)) 75%)',
    backgroundSize: '200% 100%',
    animation: 'pexels-shimmer 1.4s infinite',
  },

  loadMoreRow: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 16,
  },

  loadMoreBtn: {
    padding: '8px 24px',
    background: 'var(--panel-strong, rgba(15,23,42,0.06))',
    border: '1px solid var(--stroke, rgba(15,23,42,0.12))',
    borderRadius: 8,
    fontSize: '0.8125rem',
    color: 'var(--text, #0f172a)',
    cursor: 'pointer',
    fontWeight: 500,
  },

  retryBtn: {
    padding: '7px 18px',
    background: 'var(--primary, #2177FF)',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: '0.8125rem',
    cursor: 'pointer',
  },

  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 20px',
    textAlign: 'center',
  },

  footer: {
    padding: '10px 20px',
    borderTop: '1px solid var(--stroke, rgba(15,23,42,0.08))',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },

  attributionLink: {
    display: 'inline-flex',
    alignItems: 'center',
    color: 'var(--text-secondary, #64748b)',
    fontSize: '0.75rem',
    textDecoration: 'none',
  },
};

export default PexelsImagePicker;
